import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import flattenChildren from 'react-keyed-flatten-children';

import { useResponsiveGutterSpacing } from '../../../hooks';
import { List, ListItem } from '../../List';

export const HStack = React.memo((props: HStackProps) => {
  const {
    customSpacing,
    itemsPerRow,
    children,
    style,
    itemStyle,
    shouldFillAvailableSpace = true,
    ...rest
  } = props;

  const { wrapperSpacing, itemSpacing } = useHStackSpacerStyles(customSpacing);

  // only specify flexBasis (width) when items per row is defined, so that variable width items can be used
  const maybeItemWidth = itemsPerRow
    ? { flexBasis: `${100 / itemsPerRow}%` }
    : {};

  /**
   * Similar to React's built-in Children.toArray method, this utility takes
   * children and returns them as an array for introspection or filtering.
   * Different from Children.toArray, it will flatten arrays and React.Fragments
   * into a regular, one-dimensional array while ensuring element and fragment
   * keys are preserved, unique, and stable between renders.
   *
   * @see {@link: https://github.com/grrowl/react-keyed-flatten-children}
   */
  const flatChildren = flattenChildren(children);

  return (
    <List
      style={[
        styles.listBase,
        shouldFillAvailableSpace && styles.listBaseFill,
        wrapperSpacing.negativeOffset,
        style,
      ]}
      {...rest}
    >
      {flatChildren.map((child, index) => (
        <ListItem
          style={[itemSpacing.spaceAround, maybeItemWidth, itemStyle]}
          // eslint-disable-next-line react/no-array-index-key
          key={`stack-item-${index}`}
        >
          {child}
        </ListItem>
      ))}
    </List>
  );
});

const styles = StyleSheet.create({
  listBase: {
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  listBaseFill: {
    flex: 1,
  },
});

//
// ─── utils, etc ──────────────────────────────────────────────────────────────────
//

const useHStackSpacerStyles = (customSpacer?: number) => {
  const standardGutter = useResponsiveGutterSpacing();
  const resolvedSpacing = customSpacer ?? standardGutter;

  const wrapperSpacing = {
    negativeOffset: {
      marginTop: -resolvedSpacing / 2,
      marginBottom: -resolvedSpacing / 2,
      marginLeft: -resolvedSpacing / 2,
      marginRight: -resolvedSpacing / 2,
    },
  };

  // Individuated padding values so that they can be overwritten.
  const itemSpacing = {
    spaceAround: {
      paddingTop: resolvedSpacing / 2,
      paddingBottom: resolvedSpacing / 2,
      paddingLeft: resolvedSpacing / 2,
      paddingRight: resolvedSpacing / 2,
    },
  };

  return { wrapperSpacing, itemSpacing };
};

// ─── Props ──────────────────────────────────────────────────────────────────────

export type HStackProps = Readonly<{
  customSpacing?: number;
  itemsPerRow?: number;
  itemStyle?: ViewProps['style'];

  /**
   * Applies `flex: 1` style to fill available space (`true` by default).
   *
   * But in some circumstances, we want `HStack` to have height dependent on its
   * content rather than to fill the available area.
   */
  shouldFillAvailableSpace?: boolean;
}> &
  ViewProps;
